/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	Pagination as NativePagination,
	Group,
	Text,
	Box,
	NumberInput,
} from '@mantine/core';
import isNil from 'lodash/isNil';

// Styles
import classes from './Pagination.module.css';

// I18n
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type TPaginationProps = {
	total: number;
	totalitems?: number;
	boundaries?: number;
	activepage?: number;
	pagesize?: number;
	maxpagesize?: number;
	onChange?: (data: any) => void;
	handlechangesize: (data: string | number) => void;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export function formatPaginationInfo(
	currentPage: number | undefined,
	pageSize: number | undefined,
	totalItems: number | undefined
): string {
	const validCurrentPage = currentPage || 1;
	const validPageSize = pageSize || 10;
	const validTotalItems = totalItems || 0;

	const startIndex =
		(validCurrentPage - 1) * validPageSize + 1;
	const endIndex = Math.min(
		startIndex + validPageSize - 1,
		validTotalItems
	);

	return `${startIndex
		.toString()
		.padStart(2, '0')} – ${endIndex
		.toString()
		.padStart(2, '0')} of ${validTotalItems}`;
}

export const Pagination = (props: TPaginationProps) => {
	/* ********************************  PROPS  ********************************* */
	const { maxpagesize } = props;
	/* ********************************  HOOKS  ********************************* */
	const { t } = useTranslation();

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Group>
				<Text className={classes.selectPageNumber}>
					{t('global.linesPerPage')}
				</Text>

				<NumberInput
					classNames={classes}
					data-testid="W0FtGXR8_Z5jdHi6B-pagination-number-input"
					min={0}
					max={!isNil(maxpagesize) ? maxpagesize : 100}
					clampBehavior="strict"
					variant="unstyled"
					className={classes.selectPageNumberContainer}
					hideControls
					value={props.pagesize}
					onChange={(data: string | number) => {
						props.handlechangesize(data);
					}}
				/>

				<Text className={classes.selectPageNumber}>
					{formatPaginationInfo(
						props.activepage,
						props.pagesize,
						props.totalitems
					)}
				</Text>
			</Group>
			<Group>
				<Text className={classes.selectPageNumber}>
					{t('global.selectPage')}
				</Text>
				<Box
					className={classes.selectPageNumberContainer}
				>
					<Text className={classes.selectPageNumber}>
						{props.activepage}
					</Text>
				</Box>
				<NativePagination
					value={props.activepage}
					classNames={classes}
					onChange={props.onChange}
					total={props.total}
				/>
			</Group>
		</Box>
	);
};
